/**
 * testers hold the validation functions to be called. 
 * Each validator returns strictly a boolean value.
 */
const testers={
  'number': (val)=>typeof(val)==='number' && !isNaN(Number(val)),
  'string': (val)=>typeof(val)==='string',
  'boolean': (val)=>typeof(val)==='boolean',
  'function':(val)=>typeof(val)==='function',
  'array': (val)=>Array.isArray(val),
  'object': (val)=>val?.constructor===Object,
  'element':(val)=>val instanceof HTMLElement
}

/**
 * 
 * @param {String} type: type is the property to be picked from testers.
 *  if the type property is not found, then an error is thrown for the same. 
 */
const typeAffirmer=(type)=>{

    let tester=testers[type];

    if(!tester){ 
       throw new Error(`'${type}' type not found in typeAffirmer.`);
    }  
    
    return tester;
}

const iterate=(obj={})=>({...obj,
    *[Symbol.iterator](){
        for(let prop in this){
            yield [prop,this[prop]]
        }
    }
})
/**
 * compose_object is a curried function
 * @param {Object of Functions} methods: object containing methods to be added 
 * to the instance with close binding.
 * @param {Object} instance: instance refers to the final object to be created and returned.
 */
const compose_object=(methods)=>(instance)=>{
    let instance_methods={}
    for(let prop in methods){
       if(methods[prop] instanceof Function){
            instance_methods[prop]=methods[prop].bind(instance);
       }
       else{
            instance_methods[prop]=methods[prop]
       }
    }

   return (Object.assign(instance,instance_methods))
};

/**
 * @param {Object} obj: @returns freezes input object so that no further changes can be done. 
 */
const freeze=(obj)=>(Object.freeze(obj))

/**
 * curried function used to validate user inputs. 
 * function is used to validate the user_object with desc_object.
 * @param {Object} desc_object: object used to test the user input object.
 * @param {Object} user_object: object to be validated. 
 * 
 * @description:
 * the desc_object is a descriptor object in which each property defines the following:
 * @param {String} default: the default value of the property if no value found.
 * @param {Function} test: test to validate the value strictly @returns {boolean}
 * @param {String} name: name of the property if default property name needs to be overriddden.
 * @param {Function} warn: test to warn the user if the value doesn't meet standards.
 * all @params are optional and an empty object can be passed if no operations are required. 
 */

const validate_object=(desc_object)=>(user_object)=>{
    let output_object={}
    for(let [propname,propvalue] of iterate(desc_object)){
        let setname=propvalue.name??propname;
        //assign user value or else default value.
        output_object[setname]=user_object[propname]??propvalue.default??null;

        //test if type test passes.
        if(propvalue.test?.(output_object[setname])===false){
            throw new Error(`'${propname}' property has the wrong value passed.`);
        }
        if(propvalue.warn){
            let warning=propvalue.warn(output_object[setname]);
            warning && console.warn(warning);
        }
            
    }

    return output_object;
}

/**
 * curried function used to get default values. 
 * @param {Object} desc_object: object used to test the user input object.
 */
const get_default=(desc_object)=>()=>{
    let output_object={}
    for(let prop in desc_object){
        output_object[desc_object[prop].name??prop]=desc_object[prop].default??null;
    }
    return output_object;
}


/** parser instance to parse any string to XML,HTML */
const parser=new DOMParser();

/**
 * function used to get available subtitles a in dash source. 
 * @param {String} mpdText: string format of dash source.
 * @param {String} mpdurl: url of dash source to get path.
 * @returns {Array of Objects{lang,src}}
 */
const dashParseSubs=(mpdText,mpdurl='')=>{
    const path=mpdurl.match(/.+\//)?.[0]??'';
    const mpd=parser.parseFromString(mpdText,'application/xml');
    const text_nodes=mpd.querySelectorAll("Period>AdaptationSet[contentType='text']");
    const vtt_sources=[...text_nodes]
                        .map(node=>{
                            let lang=node.getAttribute('lang');
                            let src=node.querySelector('Representation[mimeType*="vtt"]>BaseURL')?.textContent;
                            if(src)src= path + src;
                            
                            return {lang,src}
                        })
                        .filter(node=>!!node.src)                   
    return vtt_sources;
}


export {
    typeAffirmer,
    compose_object,
    freeze,
    validate_object,
    get_default,
    dashParseSubs
}